/**
 * Presentation constants.
 *
 * Brand palette: red / black / white, sampled from the Simas Jiwa logo
 * (`src/assets/Logo_Only.png` → emblem red `#EA1B1A`, wordmark black
 * `#1A1919`). Red is a brand-chrome accent — tabs, titles, buttons, KPI
 * accents — but never used as chart data-ink (see `CHART` below) or on the
 * trend indicators. Green (`mint`) / red (`coral`) stay exactly as they were
 * for MoM/YoY up-down signals, per product requirement.
 */

/** Simas Jiwa red/black brand, plus the light editorial theme tokens. */
export const BRAND = {
  /** Primary brand red, sampled from the logo emblem. */
  red: '#EA1B1A',
  redDark: '#B4130F',
  redBright: '#FF4433',
  redSoft: '#F3A5A0',
  redTint: 'rgba(234, 27, 26, 0.08)',
  /** Wordmark black, sampled from the logo. */
  black: '#1A1919',
  blackSoft: '#3A3A3A',
  /** Neutral third series colour (charts, "neutral" insight tone) — never blue. */
  charcoal: '#4B5563',
  pageBg: '#EEF1F5',
  ink: '#1B2436',
  mint: '#17936B',
  coral: '#D4574A',
  textDim: '#55607A',
  textFaint: '#8A93A6',
  panelBorder: '#DFE4EC',
  panel: '#FFFFFF',
  track: '#E3E8F0',
  positive: '#17936B',
  negative: '#D4574A',
  muted: '#8A93A6',
} as const;

export const EDITORIAL_SHADOW = '0 1px 2px rgba(27,36,54,0.04), 0 4px 14px rgba(27,36,54,0.06)';
export const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND.black} 0%, ${BRAND.blackSoft} 100%)`;
export const RED_GRADIENT = `linear-gradient(135deg, ${BRAND.redDark}, ${BRAND.red})`;

/**
 * Chart data-ink — deliberately NOT brand red.
 *
 * Red is reserved for brand chrome (tabs, buttons, KPI accents) and for the
 * "down" trend indicator (`coral`) used on deltas throughout the page. Using
 * it again as the NAV/growth line colour would make a rising chart look like
 * an alert, and would be indistinguishable from a negative-delta signal.
 *
 * Instead each product gets its own hue, drawn from the Okabe–Ito
 * colour-blind-safe palette so IDR vs Dollar stays legible for the ~8% of
 * men with red-green colour vision deficiency, and neither hue is close to
 * `mint`/`coral`. Three tones per product, one per growth metric on the
 * combo chart (MoM/3-month as bars, YoY as the line):
 *   - SFL IDR    → blue family   (strong #0072B2 = MoM bar, mid #2B93CD = 3-month bar, soft #56B4E9 = YoY line)
 *   - SFL Dollar → amber family  (strong #E69F00 = MoM bar, mid #B87F00 = 3-month bar, soft #8A5F00 = YoY line)
 */
export const CHART = {
  idr: { strong: '#0072B2', mid: '#2B93CD', soft: '#56B4E9' },
  usd: { strong: '#E69F00', mid: '#B87F00', soft: '#8A5F00' },
} as const;

/** Sector badge colours, from the reference's PortfolioHoldings. Categorical data-viz palette — not brand chrome. */
export const SECTOR_COLORS: Record<string, string> = {
  Perbankan: '#2563EB',
  Properti: '#7C3AED',
  Infrastruktur: '#10B981',
  Multifinance: '#F59E0B',
  Pertambangan: '#EF4444',
  'Industri Dasar': '#06B6D4',
  Energi: '#EC4899',
  Farmasi: '#84CC16',
  'Pasar Uang': '#94A3B8',
  Telekomunikasi: '#F97316',
  Agribisnis: '#22C55E',
  Syariah: '#14B8A6',
};

/** Neutral fallback for a sector the ramp above has never seen. */
export const SECTOR_FALLBACK = '#94A3B8';

export function sectorColor(sector: string | null | undefined): string {
  if (!sector) return SECTOR_FALLBACK;
  return SECTOR_COLORS[sector] ?? SECTOR_FALLBACK;
}

/**
 * The year ramp: pale for the oldest year, full black for the newest. Unused
 * on the current Funds page (the year-on-year overlay was replaced by the
 * combo chart) but kept for any future per-year breakdown.
 */
const YEAR_RAMP = ['#E8D6D5', '#D8ADAB', '#C57A77', '#EA1B1A', '#B4130F', '#1A1919'] as const;

/**
 * Maps an ascending list of years to colours, oldest → newest, always ending on
 * the darkest stop so "this year" reads as the emphasised line.
 *
 * With more years than stops the ramp is interpolated by repeating intermediate
 * stops, so a 9-year fund still gets a monotonic pale→dark progression.
 */
export function yearColorScale(years: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  const n = years.length;
  if (n === 0) return out;
  if (n === 1) {
    out[years[0]!] = YEAR_RAMP[YEAR_RAMP.length - 1]!;
    return out;
  }
  years.forEach((year, i) => {
    const t = i / (n - 1); // 0 → oldest, 1 → newest
    const idx = Math.round(t * (YEAR_RAMP.length - 1));
    out[year] = YEAR_RAMP[idx]!;
  });
  return out;
}

/** The two most recent years get a thicker stroke, as in the reference. */
export function yearStrokeWidth(year: string, years: string[]): number {
  const rank = years.length - 1 - years.indexOf(year);
  return rank <= 1 ? 2.5 : 1.5;
}
